/**
 * ProtectedRoute - Auth guard component with approval status check
 *
 * Features:
 * - Redirects unauthenticated users to login
 * - Shows PendingApprovalScreen for users awaiting admin approval
 * - Shows RejectedScreen for rejected users
 * - Renders children for approved users
 */

import { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PendingApprovalScreen from './PendingApprovalScreen';
import RejectedScreen from './RejectedScreen';
import RevokedScreen from './RevokedScreen';

interface ProtectedRouteProps {
  children: ReactNode;
}

type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'revoked' | null;

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>(null);
  const [checkingApproval, setCheckingApproval] = useState(true);

  // Check approval status and subscribe to real-time changes.
  // Without the subscription, a user sitting on PendingApprovalScreen would
  // never see their access granted until they manually refreshed the page.
  useEffect(() => {
    if (!user) {
      setCheckingApproval(false);
      setApprovalStatus(null);
      return;
    }

    const fetchStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('approval_status')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.log('[ProtectedRoute] Profile not found, treating as pending');
          setApprovalStatus('pending');
        } else if (data) {
          setApprovalStatus(data.approval_status as ApprovalStatus);
        } else {
          setApprovalStatus('pending');
        }
      } catch (err) {
        console.error('[ProtectedRoute] Error checking approval status:', err);
        // Fail open for existing users so a transient error doesn't lock them out
        setApprovalStatus('approved');
      } finally {
        setCheckingApproval(false);
      }
    };

    fetchStatus();

    // Real-time: react immediately when an admin changes this user's status.
    // Covers: pending → approved, approved → revoked, revoked → approved, etc.
    const subscription = supabase
      .channel(`profile-status-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newStatus = payload.new?.approval_status as ApprovalStatus;
          if (newStatus) {
            console.log('[ProtectedRoute] Status changed to:', newStatus);
            setApprovalStatus(newStatus);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id]);

  // Show loading spinner while checking auth or approval
  if (loading || checkingApproval) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orca-elevate"></div>
          <p className="mt-4 text-steel">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    console.log('[ProtectedRoute] No user found, redirecting to login...');
    return <Navigate to="/login" replace />;
  }

  // Show pending approval screen
  if (approvalStatus === 'pending') {
    return <PendingApprovalScreen />;
  }

  // Show rejected screen (pre-activation refusal)
  if (approvalStatus === 'rejected') {
    return <RejectedScreen />;
  }

  // Show revoked screen (post-approval deactivation)
  if (approvalStatus === 'revoked') {
    return <RevokedScreen />;
  }

  // Render protected content for approved users
  return <>{children}</>;
}
